import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { utilService } from '../../../../core/services'
import { EditTableColumn, IconButton, Panel, PanelTitle } from '../../../atoms'
import { EditTable } from '../../../organisms'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import StatusTip from 'components/atoms/StatusTip'
import moment from 'moment'
import Summary from 'components/organisms/Dashboard/Summary'
import { ISales } from 'static/interfaces'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SimpleYearMonthSelect from 'components/organisms/SimpleYearMonthSelect'

declare var window: interfaces.IWindow
interface IProps {
  sales_data: ISales
  user: any
}

const reservation_options = { scope: 'activerecord.attributes.reservation' }
const COLUMNS = [
  {
    name: I18n.t('generic.status'),
    field: 'workflow_state',
    renderElement: record => (
      <EditTableColumn>
        {record.payable_type === 'Reservation' ? record.payable?.workflow_state : '-'}
      </EditTableColumn>
    ),
  },
  {
    name: I18n.t('generic.date'),
    field: 'tour_date',
    renderElement: record => <EditTableColumn>{record.payable?.tour_date}</EditTableColumn>,
  },
  {
    name: I18n.t('reservation.reservation_no'),
    field: 'payable_id',
    renderElement: record => (
      <EditTableColumn>{record.payable?.no || record.payable?.id}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('post_name', reservation_options),
    field: 'post_name',
    renderElement: record => (
      <EditTableColumn type="link">
        <a href={`/posts/${record.payable?.post.slug}`}>{record.payable?.post.name}</a>
      </EditTableColumn>
    ),
  },
  {
    name: I18n.t('num_of_people', reservation_options),
    field: 'num_of_people',
    renderElement: record => <EditTableColumn>{record.payable?.num_of_people}</EditTableColumn>,
  },
  {
    name: I18n.t('sales.amount'),
    field: 'amount',
    renderElement: record => (
      <EditTableColumn>
        {record.payable_type === 'Reservation'
          ? utilService.formatPrice(record.payable?.price)
          : '-'}
      </EditTableColumn>
    ),
  },
  {
    name: I18n.t('sales.net'),
    field: 'net',
    renderElement: record => (
      <EditTableColumn type="bold">{utilService.formatPrice(record.amount)}</EditTableColumn>
    ),
  },
]

const getStatus = (diff: number) => {
  if (diff === 0) {
    return 'flat'
  }
  if (diff < 0) {
    return 'down'
  }
  return 'up'
}
const getUnit = (diff: number) => {
  if (diff === 0) {
    return ''
  }
  if (diff < 0) {
    return '-'
  }
  return '+'
}

const Index: React.FC<IProps> = props => {
  const { data: user } = utilService.getDataFromJson(props.user)

  const urlParams = new URLSearchParams(window.location.search)
  const monthParam = urlParams.get('month')
  const currentMonth = moment(monthParam ?? undefined)
  const now = moment()

  const { amount, amount_last_month, earning, earning_last_month } = props.sales_data

  const amountLastMonth = amount_last_month
  const amountDiff = amount - amountLastMonth

  const calcEarning = earning
  const earningLastMonth = earning_last_month
  const earningDiff = calcEarning - earningLastMonth

  const prevMonth = React.useCallback(() => {
    urlParams.set('month', currentMonth.add(-1, 'month').format('YYYY-MM'))
    location.href = `/agent/sales?${urlParams.toString()}`
  }, [currentMonth])

  const nextMonth = React.useCallback(() => {
    urlParams.set('month', currentMonth.add(1, 'month').format('YYYY-MM'))
    location.href = `/agent/sales?${urlParams.toString()}`
  }, [currentMonth])

  const changeYearMonth = e => {
    const element = e.currentTarget as HTMLSelectElement | HTMLButtonElement
    urlParams.set('month', moment(element.value).format('YYYY-MM'))
    location.href = `/agent/sales?${urlParams.toString()}`
  }
  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <Panel marginType="dashboard" title={<PanelTitle title={I18n.t('sales.title')} />}>
          <div className="Amount_Date">
            <IconButton size="sm" iconName="arrow_back_ios" handleClick={prevMonth} />
            <SimpleYearMonthSelect changeYearMonth={changeYearMonth} yearMonth={currentMonth} />
            <IconButton
              size="sm"
              iconName="arrow_forward_ios"
              disabled={now.isSame(currentMonth, 'day')}
              handleClick={nextMonth}
            />
          </div>
          <div className="Summary_Top">
            <div className="Summary_Content">
              <StatusTip status={getStatus(amountDiff)} size={128} smallSize={72} label="売上" />
              <Amount>
                <div className="Amount_Current">¥{amount}</div>
                <div className="Amount_Diff">
                  <span className="Diff_Date">先月 </span>
                  <span className={`Diff_Price ${getStatus(amountDiff)}`}>
                    {getUnit(amountDiff)} ¥{Math.abs(amountDiff)}
                  </span>
                </div>
              </Amount>
            </div>
            <div className="Summary_Content">
              <StatusTip
                status={'custom'}
                size={128}
                smallSize={72}
                customIcon="paid"
                label="収益"
              />
              <Amount>
                <div className="Amount_Current">¥{calcEarning}</div>
                <div className="Amount_Diff">
                  <span className="Diff_Date">先月 </span>
                  <span className={`Diff_Price ${getStatus(earningDiff)}`}>
                    {getUnit(earningDiff)} ¥{Math.abs(earningDiff)}
                  </span>
                </div>
              </Amount>
            </div>
          </div>

          <div className="Summary_Bottom">
            <Summary data={props.sales_data.rates} />
          </div>
          <div className="Table_Section">
            <EditTable editable={false} columns={COLUMNS} records={[]} />
          </div>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .Summary_Top {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 48px;
    margin-top: 40px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      gap: 24px;
    }
  }

  .Summary_Content {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .Summary_Bottom {
    max-width: 570px;
    margin: 40px auto;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      margin: 40px auto;
    }
  }
  .Table_Section {
    margin: 80px auto 0;
  }
  .Amount_Date {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    color: ${constants.COLORS.subText};
    font-size: 28px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 18px;
    }
  }
`

const Amount = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  margin-left: 32px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    margin-left: 16px;
  }

  .Amount_Current {
    font-size: 48px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 24px;
    }
  }
  .Amount_Diff {
    font-size: 16px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 12px;
    }
    .Diff_Date {
      color: ${constants.COLORS.subText};
    }
    .Diff_Price {
      &.up {
        color: ${constants.COLORS.successText};
      }
      &.flat {
        color: ${constants.COLORS.useallyText};
      }
      &.down {
        color: ${constants.COLORS.cautionText};
      }
    }
  }
`

export default Index
